import { SlideInferenceResults } from 'interfaces/calculateFeatures';
import { filter, forEach, groupBy, head, isBoolean, isNull, isUndefined, join, size } from 'lodash';
import type { SlideManifestPair } from './types';

import { metadataOptions, type MetadataOptionKey } from './SelectSlides';
import type { SlideMetadataSide } from './SlideMetadata';

export type BuildWarningsStringArgs = {
  casesWithMultipleSourceAndTarget: string[];
  casesWithMultipleSourceOnly: string[];
  casesWithMultipleTargetOnly: string[];
};

const formatCaseListWithVerb = (caseIds: string[]) => {
  const caseCount = size(caseIds);

  if (!caseCount) return '';
  if (caseCount === 1) return `Case ${head(caseIds) ?? ''} has`;

  return `Cases ${join(caseIds, ', ')} have`;
};

export const buildWarningsString = ({
  casesWithMultipleSourceAndTarget,
  casesWithMultipleSourceOnly,
  casesWithMultipleTargetOnly,
}: BuildWarningsStringArgs) => {
  const warningParts: string[] = [];

  if (size(casesWithMultipleSourceAndTarget)) {
    warningParts.push(
      `${formatCaseListWithVerb(casesWithMultipleSourceAndTarget)} more than one source and target slide selected.`
    );
  }

  if (size(casesWithMultipleSourceOnly)) {
    warningParts.push(`${formatCaseListWithVerb(casesWithMultipleSourceOnly)} more than one source slide selected.`);
  }

  if (size(casesWithMultipleTargetOnly)) {
    warningParts.push(`${formatCaseListWithVerb(casesWithMultipleTargetOnly)} more than one target slide selected.`);
  }

  return join(warningParts, ' ');
};

export const getSlidesMatchingCriteria = ({
  slidesInCase,
  stainType,
  metadataOptionKeys,
  metadataOptionFieldName,
  watch,
}: {
  slidesInCase: SlideInferenceResults[];
  stainType: string;
  metadataOptionKeys: MetadataOptionKey[];
  metadataOptionFieldName: SlideMetadataSide;
  watch: (fieldName: string) => any;
}) => {
  return filter(slidesInCase, (slide: SlideInferenceResults) => {
    if (slide.stainingType !== stainType) {
      return false;
    }

    let isMatch = true;
    forEach(metadataOptionKeys, (optionKey) => {
      const fieldName = metadataOptions[optionKey][metadataOptionFieldName];
      const fieldValue = watch(fieldName);

      if (isBoolean(fieldValue)) {
        if (fieldValue) {
          isMatch = isMatch && slide[optionKey] === true;
        } else {
          isMatch =
            isMatch && (slide[optionKey] === false || isNull(slide[optionKey]) || isUndefined(slide[optionKey]));
        }
      }
    });

    return isMatch;
  });
};

export const createSlideManifestAndWarnings = ({
  slides,
  sourceStain,
  targetStain,
  sourceSelectedMetadataOptions,
  targetSelectedMetadataOptions,
  watch,
}: {
  slides?: SlideInferenceResults[];
  sourceStain: string;
  targetStain: string;
  sourceSelectedMetadataOptions: MetadataOptionKey[];
  targetSelectedMetadataOptions: MetadataOptionKey[];
  watch: (fieldName: string) => any;
}): { slideManifest: SlideManifestPair[]; warningsString: string } => {
  if (!slides) return { slideManifest: [], warningsString: '' };

  const slideManifest: SlideManifestPair[] = [];
  const slidesByCaseId = groupBy(slides, 'caseId');

  const casesWithMultipleSourceOnly: string[] = [];
  const casesWithMultipleTargetOnly: string[] = [];
  const casesWithMultipleSourceAndTarget: string[] = [];

  forEach(slidesByCaseId, (slidesInCase, caseId) => {
    const sourceSlides = getSlidesMatchingCriteria({
      slidesInCase,
      stainType: sourceStain,
      metadataOptionKeys: sourceSelectedMetadataOptions,
      metadataOptionFieldName: 'sourceFieldName',
      watch,
    });

    const targetSlides = getSlidesMatchingCriteria({
      slidesInCase,
      stainType: targetStain,
      metadataOptionKeys: targetSelectedMetadataOptions,
      metadataOptionFieldName: 'targetFieldName',
      watch,
    });

    forEach(sourceSlides, (sourceSlide: any) => {
      forEach(targetSlides, (targetSlide: any) => {
        if (sourceSlide.slideId !== targetSlide.slideId) {
          slideManifest.push({ slideId1: sourceSlide.slideId, slideId2: targetSlide.slideId });
        }
      });
    });

    const hasMultipleSource = size(sourceSlides) > 1;
    const hasMultipleTarget = size(targetSlides) > 1;

    if (hasMultipleSource && hasMultipleTarget) {
      casesWithMultipleSourceAndTarget.push(caseId);
    } else if (hasMultipleSource) {
      casesWithMultipleSourceOnly.push(caseId);
    } else if (hasMultipleTarget) {
      casesWithMultipleTargetOnly.push(caseId);
    }
  });

  const warningsString = buildWarningsString({
    casesWithMultipleSourceAndTarget,
    casesWithMultipleSourceOnly,
    casesWithMultipleTargetOnly,
  });

  return { slideManifest, warningsString };
};
